import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Loader2, ArrowLeft, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  useApproveReport,
  useRejectReport,
  useReportDetail,
} from '@/hooks/use-reports';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ReportMedia } from '@/components/reports/report-media';
import { RejectDialog } from '@/components/reports/reject-dialog';

export default function ReportDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: report, isLoading } = useReportDetail(id);
  const approveMutation = useApproveReport();
  const rejectMutation = useRejectReport();
  const [rejectOpen, setRejectOpen] = useState(false);

  const isPending = approveMutation.isPending || rejectMutation.isPending;
  const canDecide = report?.approval_status === 'pending';

  const handleApprove = () => {
    if (!id) return;
    approveMutation.mutate(id, {
      onSuccess: () => {
        toast.success('Report approved');
        navigate('/reports');
      },
      onError: (err: unknown) => {
        toast.error(err instanceof Error ? err.message : 'Failed to approve');
      },
    });
  };

  const handleReject = (reason: string) => {
    if (!id) return;
    rejectMutation.mutate(
      { id, input: { reason } },
      {
        onSuccess: () => {
          toast.success('Report rejected');
          setRejectOpen(false);
          navigate('/reports');
        },
        onError: (err: unknown) => {
          toast.error(err instanceof Error ? err.message : 'Failed to reject');
        },
      },
    );
  };

  if (isLoading || !report) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/reports')}>
          <ArrowLeft className="mr-1 h-4 w-4" /> Back
        </Button>
        <h1 className="text-2xl font-semibold">Report</h1>
        <Badge variant="outline">{report.approval_status}</Badge>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{report.seed_title}</CardTitle>
          <p className="text-sm text-muted-foreground">
            Submitted by <span className="font-medium">{report.student_name}</span> on{' '}
            {new Date(report.submitted_at).toLocaleString()}
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {report.report_text && (
            <div>
              <h3 className="mb-2 text-sm font-semibold uppercase text-muted-foreground">
                Text
              </h3>
              <p className="whitespace-pre-wrap text-sm">{report.report_text}</p>
            </div>
          )}
          {report.attachments.length > 0 && (
            <div>
              <h3 className="mb-2 text-sm font-semibold uppercase text-muted-foreground">
                Attachments ({report.attachments.length})
              </h3>
              <div className="space-y-3">
                {report.attachments.map((att) => (
                  <ReportMedia key={att.id} reportId={report.id} attachment={att} />
                ))}
              </div>
            </div>
          )}
          {report.rejection_reason && (
            <div>
              <h3 className="mb-2 text-sm font-semibold uppercase text-muted-foreground">
                Rejection reason
              </h3>
              <p className="text-sm text-destructive">{report.rejection_reason}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {canDecide && (
        <div className="flex gap-2">
          <Button onClick={handleApprove} disabled={isPending}>
            {approveMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            <Check className="mr-1 h-4 w-4" />
            Approve
          </Button>
          <Button variant="destructive" onClick={() => setRejectOpen(true)} disabled={isPending}>
            <X className="mr-1 h-4 w-4" />
            Reject
          </Button>
        </div>
      )}

      <RejectDialog
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        onConfirm={handleReject}
        isPending={rejectMutation.isPending}
      />
    </div>
  );
}
